class ParallaxMedia extends HTMLElement {
  constructor() {
    super();

    this.frame = null;
    this.mediaWrappers = [];
    this.isVisible = false;
    this.isTicking = false;
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.observer = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
    this.update = this.update.bind(this);
  }

  connectedCallback() {
    this.frame = this.querySelector('.parallax-media-item');
    this.mediaWrappers = Array.from(this.querySelectorAll('.parallax-media-asset-wrapper'));

    if (!this.frame || !this.mediaWrappers.length) {
      return;
    }

    this.reduceMotionQuery.addEventListener('change', this.handleMotionChange);

    if (this.reduceMotionQuery.matches) {
      this.setOffset(0);
      return;
    }

    this.initObserver();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    this.requestUpdate();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.reduceMotionQuery.removeEventListener('change', this.handleMotionChange);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isVisible = true;
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.isVisible = entry.isIntersecting;
        if (this.isVisible) {
          this.requestUpdate();
        }
      });
    }, {
      rootMargin: '200px 0px'
    });

    this.observer.observe(this);
  }

  handleScroll() {
    if (!this.isVisible) {
      return;
    }

    this.requestUpdate();
  }

  handleResize() {
    this.requestUpdate();
  }

  handleMotionChange() {
    if (this.reduceMotionQuery.matches) {
      this.setOffset(0);
      return;
    }

    this.requestUpdate();
  }

  requestUpdate() {
    if (this.isTicking) {
      return;
    }

    this.isTicking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.isTicking = false;

    if (!this.frame || this.reduceMotionQuery.matches) {
      this.setOffset(0);
      return;
    }

    var rect = this.frame.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var progress = ((rect.top + rect.height / 2) - viewportHeight / 2) / (viewportHeight / 2 + rect.height / 2);
    var range = parseFloat(this.dataset.parallaxRange || '80');
    var strength = parseFloat(this.dataset.parallaxStrength || '1');
    var offset = Math.max(-range, Math.min(range, progress * range * strength * -1));

    this.setOffset(offset);
  }

  setOffset(offset) {
    this.mediaWrappers.forEach((wrapper) => {
      wrapper.style.setProperty('--parallax-offset', offset.toFixed(2));
    });
  }
}

if (!customElements.get('parallax-media')) {
  customElements.define('parallax-media', ParallaxMedia);
}
